import { useState } from "react";

import type { HybridSavingsSummary } from "../../../functions/_lib/savings";
import { localeOf, useLang, useT, type Lang } from "../../i18n/language";

function formatNumber(n: number, lang: Lang): string {
  return new Intl.NumberFormat(localeOf(lang), { maximumFractionDigits: 0 }).format(n);
}

interface Props {
  hybrid: HybridSavingsSummary;
}

/**
 * Hybrid (Besparingscheck) savings card for the unified tool. Rebuilt in the
 * recommender's plain-CSS design system (no portal Tailwind/Radix). Renders the
 * gas saved, CO2 reduction, extra electricity and the recommended Hybrid
 * configuration for the current product (V2).
 */
export function HybridSavingsCard({ hybrid }: Props) {
  const t = useT();
  const { lang } = useLang();
  const [showVersions, setShowVersions] = useState(false);

  const stats = [
    { key: "gas", label: "Gasbesparing", value: `${formatNumber(hybrid.gas_m3_saved, lang)} m³`, perYear: true },
    { key: "co2", label: "CO₂-reductie", value: `${formatNumber(hybrid.co2_savings_kg, lang)} kg`, perYear: true },
    { key: "stroom", label: "Extra elektriciteit", value: `${formatNumber(hybrid.extra_electricity_kwh, lang)} kWh`, perYear: true },
    { key: "scop", label: "SCOP", value: hybrid.scop.toFixed(2).replace(".", lang === "en" ? "." : ","), perYear: false },
  ];

  return (
    <section className="card" style={{ opacity: 1, transform: "none", animation: "none" }}>
      <div className="eyebrow">{t("Hybride besparing")}</div>
      <h2 className="section-title" style={{ marginTop: 8, marginBottom: 6 }}>
        {t(hybrid.recommended_product)}
      </h2>
      <p className="card-subtitle" style={{ marginTop: 0, marginBottom: 20 }}>
        {t("Geschatte jaarlijkse besparing met een Quatt Hybrid op basis van de ingevulde woninggegevens.")}
      </p>

      <div className="results-stats-card">
        {stats.map((stat) => (
          <div className="stat-item" key={stat.key}>
            <div className="stat-label-row">
              <div className="stat-label" style={{ color: "var(--muted)" }}>
                {t(stat.label)}
                {stat.perYear ? ` ${t("per jaar")}` : ""}
              </div>
            </div>
            <div className="stat-value" style={{ color: "var(--ink)" }}>{stat.value}</div>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="btn-secondary"
        style={{ marginTop: 16 }}
        onClick={() => setShowVersions((v) => !v)}
      >
        {showVersions ? t("Verberg versievergelijking") : t("Vergelijk versies")}
      </button>

      {showVersions && (
        <div className="results-stats-card" style={{ marginTop: 16 }}>
          {(["v1_5", "v2"] as const).map((version) => {
            const block = hybrid.version_breakdown[version];
            const chosen = block.recommended === "duo" ? block.duo : block.single;
            return (
              <div className="stat-item" key={version}>
                <div className="stat-label" style={{ color: "var(--muted)" }}>
                  {version === "v2" ? t("Quatt Hybrid V2") : t("Quatt Hybrid V1.5")}
                  {` (${block.recommended === "duo" ? t("Duo") : t("Solo")})`}
                </div>
                <div className="stat-value" style={{ color: "var(--ink)" }}>
                  {formatNumber(chosen.gas_m3_saved, lang)} m³ {t("gas")}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
